/**
 * Screenshots API — capture, list, download, delete viewer screenshots
 * persisted per project. Backend: `Gontec-Mobile-Django/screenshots/`.
 *
 * Endpoint contract:
 *   GET    /api/projects/<id>/screenshots/                → { items: ServerScreenshot[] }
 *   POST   /api/projects/<id>/screenshots/                → ServerScreenshot (201)
 *                                  multipart: { image: <Blob>, label?: string }
 *   DELETE /api/projects/<id>/screenshots/<sid>/          → 204
 */

import { authenticatedFetch } from '../auth/webAuthClient'
import type { AnnotationAuthor } from './annotationsClient'

export interface ServerScreenshot {
  id: string
  project_id: string
  label: string
  size_bytes: number
  created_by: AnnotationAuthor | null
  created_at: string
  /** Public DO Spaces URL — same shape across users (no signed URLs in v1). */
  url: string | null
}

export async function listScreenshots(projectId: string): Promise<ServerScreenshot[]> {
  const resp = await authenticatedFetch(
    `/api/projects/${encodeURIComponent(projectId)}/screenshots/`,
  )
  if (!resp.ok) {
    throw new Error(`Failed to list screenshots: HTTP ${resp.status}`)
  }
  const data = (await resp.json()) as { items?: ServerScreenshot[] }
  return data.items ?? []
}

export async function uploadScreenshot(
  projectId: string,
  blob: Blob,
  label?: string,
): Promise<ServerScreenshot> {
  const fd = new FormData()
  const ext = blob.type === 'image/png' ? 'png' : 'jpg'
  fd.append('image', blob, `screenshot.${ext}`)
  if (label) fd.append('label', label)
  const resp = await authenticatedFetch(
    `/api/projects/${encodeURIComponent(projectId)}/screenshots/`,
    { method: 'POST', body: fd },
  )
  if (!resp.ok) {
    // Try to surface the backend's detail message so the toast shows
    // something more useful than a bare HTTP code.
    let detail = ''
    try {
      const body = await resp.json()
      detail = body?.detail ? `: ${body.detail}` : ''
    } catch {
      try { detail = `: ${(await resp.text()).slice(0, 200)}` } catch { /* noop */ }
    }
    throw new Error(`Failed to upload screenshot: HTTP ${resp.status}${detail}`)
  }
  return (await resp.json()) as ServerScreenshot
}

/** Ask the backend for a short-lived presigned URL that forces the
 * browser to download (instead of opening in a new tab) by way of the
 * `Content-Disposition: attachment` header. Cross-origin `<a download>`
 * is ignored by browsers; this endpoint sidesteps that limitation. */
export async function getScreenshotDownloadUrl(
  projectId: string,
  id: string,
): Promise<{ url: string; filename: string }> {
  const resp = await authenticatedFetch(
    `/api/projects/${encodeURIComponent(projectId)}/screenshots/${encodeURIComponent(id)}/download/`,
  )
  if (!resp.ok) {
    throw new Error(`Failed to mint download URL: HTTP ${resp.status}`)
  }
  return (await resp.json()) as { url: string; filename: string }
}

export async function deleteScreenshot(projectId: string, id: string): Promise<void> {
  const resp = await authenticatedFetch(
    `/api/projects/${encodeURIComponent(projectId)}/screenshots/${encodeURIComponent(id)}/`,
    { method: 'DELETE' },
  )
  if (resp.status === 204 || resp.status === 404) return
  throw new Error(`Failed to delete screenshot: HTTP ${resp.status}`)
}
